import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import AOS from 'aos';
import { FiLinkedin, FiChevronRight, FiChevronLeft } from 'react-icons/fi';
import { ABOUT_CONTENT, ABOUT_VALUES, ABOUT_STORY, TEAM_LEADERSHIP, TEAM_CORE } from './About.js';
import Navbar from '../../components/Navbar/Navbar.jsx';
import Footer from '../../components/Footer/Footer.jsx';
import './About.css';

const About = () => {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const vsScrollRef = useRef(null);
  const [vsCanScrollLeft, setVsCanScrollLeft] = useState(false);
  const [vsCanScrollRight, setVsCanScrollRight] = useState(true);
  const [vsActiveDot, setVsActiveDot] = useState(0);
  const [vsTotalSlides, setVsTotalSlides] = useState(0);
  const [teamActiveDot, setTeamActiveDot] = useState(0);
  const [teamTotalSlides, setTeamTotalSlides] = useState(0);

  useEffect(() => {
    AOS.init({ duration: 800, easing: 'ease-out-cubic', once: true, offset: 60 });
  }, []);

  const updateScrollButtons = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);

    const cards = el.querySelectorAll('.ab__team-premium-leader-card');
    if (cards.length) {
      const cardWidth = cards[0].offsetWidth + 32;
      const index = Math.round(el.scrollLeft / cardWidth);
      setTeamActiveDot(Math.min(index, cards.length - 1));
    }
  };

  const scroll = (dir) => {
    const el = scrollRef.current;
    if (!el) return;
    const card = el.querySelector('.ab__team-premium-leader-card');
    const cardWidth = card ? card.offsetWidth + 32 : 480;
    el.scrollBy({ left: dir * cardWidth, behavior: 'smooth' });
    setTimeout(updateScrollButtons, 400);
  };

  const scrollTo = (index) => {
    const el = scrollRef.current;
    if (!el) return;
    const cards = el.querySelectorAll('.ab__team-premium-leader-card');
    if (cards[index]) {
      cards[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
    setTimeout(updateScrollButtons, 400);
  };

  const vsUpdateScrollButtons = () => {
    const el = vsScrollRef.current;
    if (!el) return;
    setVsCanScrollLeft(el.scrollLeft > 0);
    setVsCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);

    const cards = el.querySelectorAll('.ab__vs-card');
    if (cards.length) {
      const cardWidth = cards[0].offsetWidth + 24;
      const index = Math.round(el.scrollLeft / cardWidth);
      setVsActiveDot(Math.min(index, cards.length - 1));
    }
  };

  const vsScroll = (dir) => {
    const el = vsScrollRef.current;
    if (!el) return;
    const card = el.querySelector('.ab__vs-card');
    const cardWidth = card ? card.offsetWidth + 24 : 380;
    el.scrollBy({ left: dir * cardWidth, behavior: 'smooth' });
    setTimeout(vsUpdateScrollButtons, 400);
  };

  const vsScrollTo = (index) => {
    const el = vsScrollRef.current;
    if (!el) return;
    const cards = el.querySelectorAll('.ab__vs-card');
    if (cards[index]) {
      cards[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
    setTimeout(vsUpdateScrollButtons, 400);
  };

  useEffect(() => {
    const el = vsScrollRef.current;
    if (el) {
      setVsTotalSlides(el.children.length);
    }
    const teamEl = scrollRef.current;
    if (teamEl) {
      setTeamTotalSlides(teamEl.children.length);
    }
  }, []);

  return (
    <main>
      <Navbar />

      {/* Header — Full-width Hero Banner */}
      <section className="ab__hero">
        <div className="ab__hero-content" data-aos="fade-up">
          <h1 className="ab__hero-heading">Unforgettable <span className="text-gradient">Vietnam</span></h1>
          <p className="ab__hero-intro">{ABOUT_CONTENT.intro}</p>
        </div>
      </section>

      {/* ── Premium Our Story Section ── */}
      <section className="ab__story-premium">
        <div className="ab__story-premium-bg" />
        <div className="rt__container">
          <div className="ab__story-premium-grid">
            {/* Left: Content */}
            <div className="ab__story-premium-content" data-aos="fade-right" data-aos-anchor-placement="top-bottom">
              <h2 className="ab__story-premium-heading">Vietnam, Designed <span className="text-gradient">by Experts</span></h2>
              {ABOUT_STORY.paragraphs.map((para, i) => (
                <p key={i} className="ab__story-premium-para">{para}</p>
              ))}
            </div>
            {/* Right: Image with layered frames */}
            <div className="ab__story-premium-visual" data-aos="fade-left" data-aos-delay="200">
              <div className="ab__story-premium-image-group">
                <div className="ab__story-premium-image-wrap">
                  <img
                    src={ABOUT_STORY.image}
                    alt={ABOUT_STORY.imageAlt}
                    className="ab__story-premium-image"
                    loading="lazy"
                  />
                </div>
                <div className="ab__story-premium-overlay">
                  <img
                    src="https://images.unsplash.com/photo-1602646993875-70bc0ba52c87?q=80&w=400&auto=format&fit=crop"
                    alt="Golden Bridge, Da Nang"
                    className="ab__story-premium-overlay-img"
                    loading="lazy"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="ab rt__section">
        <div className="rt__container">
          <h2 className="ab__values-heading">Our Signature <span className="text-gradient">Approach</span></h2>
          <p className="ab__values-subheading">Every journey we design reflects our commitment to precision, care, and local mastery.</p>
          <div className="ab__vs-slider">
            <button
              className={`ab__vs-slider-btn ab__vs-slider-btn--left ${vsCanScrollLeft ? 'is-visible' : ''}`}
              onClick={() => vsScroll(-1)}
              aria-label="Previous"
            >
              <FiChevronLeft />
            </button>

            <div className="ab__vs-grid" ref={vsScrollRef} onScroll={vsUpdateScrollButtons}>
              {/* Mission */}
              <div className="ab__vs-card" data-aos="fade-up">
                <div className="ab__vs-card-img-wrap">
                  <img
                    src="https://images.unsplash.com/photo-1552664730-d307ca884978?q=80&w=600&auto=format&fit=crop"
                    alt="Our Mission"
                    className="ab__vs-card-img"
                    loading="lazy"
                  />
                </div>
                <div className="ab__vs-card-body">
                  <h3 className="ab__vs-card-title">Our Mission</h3>
                  <p className="ab__vs-card-desc">{ABOUT_CONTENT.mission}</p>
                </div>
              </div>

              {/* Vision */}
              <div className="ab__vs-card" data-aos="fade-up" data-aos-delay="80">
                <div className="ab__vs-card-img-wrap">
                  <img
                    src="https://images.unsplash.com/photo-1488190211105-8b0e65b80b4e?q=80&w=600&auto=format&fit=crop"
                    alt="Our Vision"
                    className="ab__vs-card-img"
                    loading="lazy"
                  />
                </div>
                <div className="ab__vs-card-body">
                  <h3 className="ab__vs-card-title">Our Vision</h3>
                  <p className="ab__vs-card-desc">{ABOUT_CONTENT.vision}</p>
                </div>
              </div>

              {/* Values */}
              {ABOUT_VALUES.map((val, index) => {
                const images = [
                  'https://images.unsplash.com/photo-1641891848165-24d3e124d82c?q=80&w=600&auto=format&fit=crop',
                  'https://images.unsplash.com/photo-1600880292203-757bb62b4baf?q=80&w=600&auto=format&fit=crop',
                  'https://images.unsplash.com/photo-1544027993-37dbfe43562a?q=80&w=600&auto=format&fit=crop',
                  'https://images.unsplash.com/photo-1654588836487-6455d85c2211?q=80&w=600&auto=format&fit=crop',
                ];
                return (
                  <div key={val.id} className="ab__vs-card" data-aos="fade-up" data-aos-delay={(index + 2) * 80}>
                    <div className="ab__vs-card-img-wrap">
                      <img
                        src={images[index]}
                        alt={val.title}
                        className="ab__vs-card-img"
                        loading="lazy"
                      />
                    </div>
                    <div className="ab__vs-card-body">
                      <h3 className="ab__vs-card-title">{val.title}</h3>
                      <p className="ab__vs-card-desc">{val.description}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <button
              className={`ab__vs-slider-btn ab__vs-slider-btn--right ${vsCanScrollRight ? 'is-visible' : ''}`}
              onClick={() => vsScroll(1)}
              aria-label="Next"
            >
              <FiChevronRight />
            </button>
          </div>

          <div className="ab__vs-dots">
            {Array.from({ length: vsTotalSlides }).map((_, i) => (
              <button
                key={i}
                className={`ab__vs-dot ${i === vsActiveDot ? 'ab__vs-dot--active' : ''}`}
                onClick={() => vsScrollTo(i)}
                aria-label={`Go to slide ${i + 1}`}
              />
            ))}
          </div>

          <section className="ab__team-premium">
            {/* ── Premium Leadership & Team Section ── */}

            <div className="ab__team-premium-header" data-aos="fade-up">
              <h2 className="ab__team-premium-heading">The People Behind <span className="text-gradient">the Journey</span></h2>
              <p className="ab__team-premium-desc">
                Behind every exceptional journey is a team of local experts dedicated to delivering seamless, authentic travel experiences.
              </p>
            </div>

            {/* ── Leadership Cards Slider ── */}
            <div className="ab__team-premium-slider">
              <button
                className={`ab__team-premium-slider-btn ab__team-premium-slider-btn--left ${canScrollLeft ? 'is-visible' : ''}`}
                onClick={() => scroll(-1)}
                aria-label="Previous"
              >
                <FiChevronLeft />
              </button>

              <div
                className="ab__team-premium-leadership-grid"
                ref={scrollRef}
                onScroll={updateScrollButtons}
              >
                {TEAM_LEADERSHIP.map((leader, index) => (
                  <div
                    key={leader.id}
                    className="ab__team-premium-leader-card"
                    data-aos="fade-up"
                    data-aos-delay={index * 120}
                  >
                    <div className="ab__team-premium-leader-image-wrap">
                      <img
                        src={leader.image}
                        alt={leader.name}
                        className="ab__team-premium-leader-image"
                        loading="lazy"
                      />
                    </div>
                    <div className="ab__team-premium-leader-info">
                      <h3 className="ab__team-premium-leader-name">{leader.name}</h3>
                      <span className="ab__team-premium-leader-role">{leader.role}</span>
                      <p className="ab__team-premium-leader-bio">{leader.bio}</p>
                      <a
                        href={leader.linkedin}
                        className="ab__team-premium-leader-linkedin"
                        aria-label={`${leader.name} on LinkedIn`}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        <FiLinkedin />
                      </a>
                    </div>
                  </div>
                ))}
              </div>

              <button
                className={`ab__team-premium-slider-btn ab__team-premium-slider-btn--right ${canScrollRight ? 'is-visible' : ''}`}
                onClick={() => scroll(1)}
                aria-label="Next"
              >
                <FiChevronRight />
              </button>
            </div>

            <div className="ab__vs-dots">
              {Array.from({ length: teamTotalSlides }).map((_, i) => (
                <button
                  key={i}
                  className={`ab__vs-dot ${i === teamActiveDot ? 'ab__vs-dot--active' : ''}`}
                  onClick={() => scrollTo(i)}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>

            {/* ── Core Team Grid ── */}
            <div className="ab__team-premium-core">
              <div className="ab__team-premium-core-header" data-aos="fade-up">
                <h3 className="ab__team-premium-core-heading">Meet the <span className="text-gradient">Wider Team</span></h3>
              </div>
              <div className="ab__team-premium-core-grid">
                {TEAM_CORE.map((member, index) => (
                  <div
                    key={member.id}
                    className="ab__team-premium-core-card"
                    data-aos="fade-up"
                    data-aos-delay={index * 80}
                  >
                    <div className="ab__team-premium-core-image-wrap">
                      <img
                        src={member.image}
                        alt={member.name}
                        className="ab__team-premium-core-image"
                        loading="lazy"
                      />
                    </div>
                    <h4 className="ab__team-premium-core-name">{member.name}</h4>
                    <span className="ab__team-premium-core-role">{member.role}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* ── View Full Team CTA ── */}
            <div className="ab__team-premium-cta" data-aos="fade-up">
              <Link to="/team" className="ab__team-premium-cta-btn">
                View Full Team <FiChevronRight />
              </Link>
            </div>
          </section>


        </div>
      </section>
      <Footer />
    </main>
  );
};

export default About;
